import React, { useRef, useState, useEffect } from 'react';

interface BackgroundLayerProps {
  videoSrc?: string;
  fallbackImageSrc?: string;
  posterImageSrc?: string;
  isMobile: boolean;
  className?: string;
}

export function BackgroundLayer({
  videoSrc,
  fallbackImageSrc = '/images/hero-background.jpg',
  posterImageSrc = '/images/hero-poster.jpg',
  isMobile,
  className = ''
}: BackgroundLayerProps) {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 重置状态当props变化时
  useEffect(() => {
    setVideoLoaded(false);
    setVideoError(false);
    setImageLoaded(false);
    setIsVisible(false);
  }, [videoSrc, fallbackImageSrc, isMobile]);

  // Intersection Observer 懒加载
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect(); // 加载一次后断开观察
          }
        });
      },
      {
        threshold: 0.1, // 当10%的元素可见时触发
        rootMargin: '50px' // 提前50px开始加载
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // 内存管理：页面隐藏时暂停视频
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (videoRef.current) {
        if (document.hidden) {
          videoRef.current.pause();
        } else if (isVisible) {
          // 在移动端和桌面端都尝试播放
          videoRef.current.play().catch(() => {
            // 忽略autoplay失败的错误（移动端可能被浏览器阻止）
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isVisible]);

  // 视频加载处理
  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const handleVideoError = () => {
    setVideoError(true);
  };

  // 图片加载处理
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    console.warn('背景图片加载失败:', fallbackImageSrc);
  };

  // 检查是否为YouTube视频
  const isYouTubeVideo = videoSrc && videoSrc.includes('youtube.com/watch?v=');

  // 转换YouTube URL为嵌入格式
  const getYouTubeEmbedUrl = (url: string) => {
    const videoId = url.split('v=')[1]?.split('&')[0];
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&disablekb=1&iv_load_policy=3&fs=0&start=0`;
    }
    return url;
  };

  // 是否应该显示视频 - 现在在移动端也播放
  const shouldShowVideo = videoSrc && !videoError;

  return (
    <div ref={containerRef} className={`absolute inset-0 ${className}`}>
      {/* 视频背景 - 在可见时加载（包括移动端） */}
      {shouldShowVideo && isVisible && (
        isYouTubeVideo ? (
          <iframe
            src={getYouTubeEmbedUrl(videoSrc)}
            className="pointer-events-none absolute inset-0 w-[150vw] h-[150vh] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="ZWHEEL Hero Video"
            onLoad={handleVideoLoad}
            onError={handleVideoError}
            style={{
              opacity: videoLoaded ? 1 : 0,
              transition: 'opacity 0.5s ease-in-out'
            }}
          />
        ) : (
          <video
            ref={videoRef}
            className="pointer-events-none absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            controls={false}
            disablePictureInPicture
            disableRemotePlayback
            controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
            onLoadedData={handleVideoLoad}
            onError={handleVideoError}
            poster={posterImageSrc}
            preload="metadata"
            style={{
              opacity: videoLoaded ? 1 : 0,
              transition: 'opacity 0.5s ease-in-out'
            }}
          >
            {videoSrc && <source src={videoSrc} type="video/mp4" />}
            Su navegador no soporta la etiqueta de video.
          </video>
        )
      )}

      {/* 图片背景（移动端、视频错误或视频未加载时） */}
      {(!shouldShowVideo || videoError || !isVisible) && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: isVisible ? `url(${fallbackImageSrc})` : 'none',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.5s ease-in-out'
          }}
          onLoad={handleImageLoad}
          onError={handleImageError}
          role="img"
          aria-label="Imagen de fondo del producto ZWHEEL"
        />
      )}

      {/* 加载状态指示器 */}
      {isVisible && !videoLoaded && !imageLoaded && !videoError && (
        <div className="absolute inset-0 bg-zwheel-gray-900 animate-pulse" />
      )}

      {/* 遮罩层 - 只在非移动端显示，用于文字可读性 */}
      {!isMobile && (
        <>
          {/* 深色遮罩层 */}
          <div className="absolute inset-0 bg-black/40" />

          {/* 渐变遮罩（可选，用于更好的文字可读性） */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/10" />
        </>
      )}

      {/* 移动端轻微遮罩 - 保持品牌一致性但不影响视频观赏 */}
      {isMobile && (
        <div className="absolute inset-0 bg-black/10" />
      )}
    </div>
  );
}
